import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { UserSquare2, Eye, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HRApplications = () => {
  const { getAuthHeader } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/hr/applications`, {
        headers: getAuthHeader()
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (appId, status) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/hr/applications/${appId}/status`,
        null,
        {
          headers: getAuthHeader(),
          params: { status }
        }
      );
      alert(`Application status updated to ${status}`);
      fetchApplications();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      submitted: 'bg-blue-100 text-blue-800',
      under_review: 'bg-yellow-100 text-yellow-800',
      shortlisted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      hired: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredApps = filter === 'all' ? applications : applications.filter(a => a.status === filter);

  if (loading) return <div>Loading applications...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <UserSquare2 size={28} />
            Job Applications
          </h2>
          <p className="text-gray-600 mt-1">Total: {applications.length} applications</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2 flex-wrap">
            {['all', 'submitted', 'under_review', 'shortlisted', 'rejected', 'hired'].map(status => (
              <Button
                key={status}
                size="sm"
                variant={filter === status ? 'default' : 'outline'}
                onClick={() => setFilter(status)}
              >
                {status.replace('_', ' ').toUpperCase()}
                {status !== 'all' && (
                  <Badge className="ml-2" variant="secondary">
                    {applications.filter(a => a.status === status).length}
                  </Badge>
                )}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Applications List */}
      <Card>
        <CardHeader>
          <CardTitle>Applications ({filteredApps.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredApps.map((app) => (
              <div key={app.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-bold text-lg">{app.name}</h4>
                      <Badge className={getStatusColor(app.status)}>
                        {app.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                    <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-600">
                      <div><span className="font-medium">Position:</span> {app.job_title}</div>
                      <div><span className="font-medium">Email:</span> {app.email}</div>
                      <div><span className="font-medium">Phone:</span> {app.phone}</div>
                      <div><span className="font-medium">Applied:</span> {new Date(app.created_at).toLocaleDateString()}</div>
                    </div>
                    {app.cover_letter && (
                      <div className="mt-2 text-sm text-gray-700">
                        <span className="font-medium">Cover Letter:</span>
                        <p className="mt-1 italic">"{app.cover_letter.substring(0, 150)}{app.cover_letter.length > 150 ? '...' : ''}"</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {app.status === 'submitted' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => updateStatus(app.id, 'under_review')}
                        className="bg-yellow-600 hover:bg-yellow-700"
                      >
                        Mark Under Review
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateStatus(app.id, 'shortlisted')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle size={16} className="mr-1" />
                        Shortlist
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(app.id, 'rejected')}
                        className="text-red-600"
                      >
                        <XCircle size={16} className="mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                  {app.status === 'under_review' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => updateStatus(app.id, 'shortlisted')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle size={16} className="mr-1" />
                        Shortlist
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(app.id, 'rejected')}
                        className="text-red-600"
                      >
                        <XCircle size={16} className="mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                  {app.status === 'shortlisted' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => updateStatus(app.id, 'hired')}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        Mark as Hired
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(app.id, 'rejected')}
                        className="text-red-600"
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {(app.status === 'rejected' || app.status === 'hired') && (
                    <span className="text-sm text-gray-500 py-1">No actions available</span>
                  )}
                </div>
              </div>
            ))}
            {filteredApps.length === 0 && (
              <p className="text-center py-8 text-gray-500">No applications found</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HRApplications;